import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Plus, Edit2, Trash2, X, Save, Search } from "lucide-react";
import KrakenLoader from "@/components/KrakenLoader";

const CATEGORIES = [
  ["team_dues", "Team Dues"], ["uniform_fees", "Uniform Fees"], ["tournament_fees", "Tournament Fees"],
  ["camps", "Camps"], ["sponsorships", "Sponsorships"], ["donations", "Donations"],
  ["merch", "Merch"], ["other", "Other"],
];
const STATUSES = [
  ["unpaid", "Unpaid"], ["paid", "Paid"], ["partial", "Partial"], ["overdue", "Overdue"], ["waived", "Waived"],
];
const METHODS = [["", "—"], ["stripe", "Stripe"], ["cash", "Cash"], ["check", "Check"], ["offline", "Offline"], ["waived", "Waived"]];

const CAT_LABEL = Object.fromEntries(CATEGORIES);

const STATUS_COLOR = {
  paid: "bg-green-500/15 text-green-300 border-green-400/40",
  partial: "bg-yellow-500/15 text-yellow-300 border-yellow-400/40",
  unpaid: "bg-slate-500/15 text-slate-400 border-slate-400/30",
  overdue: "bg-red-500/15 text-red-300 border-red-400/40",
  waived: "bg-blue-500/15 text-blue-300 border-blue-400/40",
};

const blankForm = {
  title: "", description: "", category: "team_dues", amount: "", due_date: "",
  status: "unpaid", amount_paid: 0, payment_date: "", payment_method: "",
  admin_notes: "", player_name: "",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 ${STATUS_COLOR[status] || ""}`}>
      {status}
    </span>
  );
}

export default function ManagePayments() {
  const { userId } = useParams();
  const [account, setAccount] = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ total_assigned: 0, total_paid: 0, balance_due: 0, overdue_amount: 0, count: 0 });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dueFilter, setDueFilter] = useState("all"); // all|overdue|this_month|future

  const load = async () => {
    setLoading(true);
    try {
      const [a, p, s] = await Promise.all([
        api.get(`/admin/accounts/${userId}`),
        api.get(`/admin/payments?user_id=${userId}`),
        api.get(`/admin/payments/summary/${userId}`),
      ]);
      setAccount(a.data); setPayments(p.data);
      setSummary({ overdue_amount: 0, ...s.data });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    const monthEnd = (() => {
      const d = new Date();
      d.setMonth(d.getMonth() + 1, 0);
      return d.toISOString().slice(0, 10);
    })();
    return payments.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
      if (dueFilter === "overdue" && p.status !== "overdue") return false;
      if (dueFilter === "this_month") {
        if (!p.due_date || p.due_date < today || p.due_date > monthEnd) return false;
      }
      if (dueFilter === "future") {
        if (!p.due_date || p.due_date <= today) return false;
      }
      if (needle) {
        const hay = [
          p.title, p.description, p.admin_notes, p.player_name,
          account?.name, account?.player_name, p.category, p.status,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [payments, q, statusFilter, categoryFilter, dueFilter, account]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      amount: Number(editing.amount || 0),
      amount_paid: Number(editing.amount_paid || 0),
      user_id: userId,
      player_name: editing.player_name || account?.player_name || "",
    };
    try {
      if (editing.id) {
        const { id, ...patch } = payload;
        await api.put(`/admin/payments/${id}`, patch);
        toast.success("Payment updated");
      } else {
        await api.post(`/admin/payments`, payload);
        toast.success("Payment added");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const quickStatus = async (p, status) => {
    try {
      const patch = { status };
      if (status === "paid") {
        patch.amount_paid = p.amount;
        patch.payment_date = new Date().toISOString().slice(0, 10);
      } else if (status === "unpaid") {
        patch.amount_paid = 0; patch.payment_date = "";
      } else if (status === "waived") {
        patch.amount_paid = 0; patch.payment_date = "";
        patch.payment_method = "waived";
      }
      await api.put(`/admin/payments/${p.id}`, patch);
      toast.success(`Marked ${status}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Archive "${p.title}"? It will be hidden from the family but history is kept.`)) return;
    try { await api.delete(`/admin/payments/${p.id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <KrakenLoader label="Loading payments…" testId="manage-pay-loading" />;

  const noFiltersActive =
    !q && statusFilter === "all" && categoryFilter === "all" && dueFilter === "all";

  return (
    <div data-testid="manage-payments-page">
      <Link to="/admin/accounts" className="btn-ghost text-sm mb-4" data-testid="back-to-accounts">
        <ChevronLeft size={14} /> Back to Accounts
      </Link>
      <div className="eyebrow mb-1">Manage Payments</div>
      <h1 className="font-display text-3xl uppercase mb-1">{account?.name}</h1>
      <div className="text-slate-400 text-sm mb-6">
        {account?.email}{account?.player_name && ` · Player: ${account.player_name}`}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="payments-summary">
        {[
          ["Total Assigned", `$${Number(summary.total_assigned).toFixed(2)}`, "text-white"],
          ["Total Paid", `$${Number(summary.total_paid).toFixed(2)}`, "text-green-300"],
          ["Balance Due", `$${Number(summary.balance_due).toFixed(2)}`, summary.balance_due > 0 ? "text-[#00E5FF]" : "text-slate-400"],
          ["Overdue", `$${Number(summary.overdue_amount).toFixed(2)}`, summary.overdue_amount > 0 ? "text-red-300" : "text-slate-400"],
        ].map(([l, v, c]) => (
          <div key={l} className="kr-card rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{l}</div>
            <div className={`font-display text-2xl ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setEditing({ ...blankForm })} className="btn-primary text-sm" data-testid="add-payment-btn">
          <Plus size={14} /> Add Payment Request
        </button>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, player, notes…"
            className="w-full pl-9 pr-3 py-2 bg-[#050A15] border border-white/10 rounded-md text-sm"
            data-testid="payments-search"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="filter-status">
          <option value="all">All statuses</option>
          {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="filter-category">
          <option value="all">All categories</option>
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={dueFilter} onChange={(e) => setDueFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="filter-due">
          <option value="all">Any due date</option>
          <option value="overdue">Overdue</option>
          <option value="this_month">Due this month</option>
          <option value="future">Future</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="kr-card rounded-xl p-12 text-center text-slate-500" data-testid="payments-empty">
          {noFiltersActive ? "No payment requests yet." : "No payments match these filters."}
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-3 lg:hidden">
            {filtered.map((p) => (
              <div key={p.id} className="kr-card rounded-xl p-4" data-testid={`payment-row-${p.id}-mobile`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{p.title}</div>
                    {p.description && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{p.description}</div>}
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-3">
                  <div><span className="text-slate-500">Category:</span> <span className="text-slate-300">{CAT_LABEL[p.category] || p.category}</span></div>
                  <div><span className="text-slate-500">Due:</span> <span className="text-slate-300">{p.due_date || "—"}</span></div>
                  <div><span className="text-slate-500">Amount:</span> <span className="text-white">${Number(p.amount).toFixed(2)}</span></div>
                  <div><span className="text-slate-500">Paid:</span> <span className="text-green-300">${Number(p.amount_paid || 0).toFixed(2)}</span></div>
                  {Number(p.remaining_balance) > 0 && (
                    <div className="col-span-2"><span className="text-slate-500">Remaining:</span> <span className="text-[#00E5FF]">${Number(p.remaining_balance).toFixed(2)}</span></div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  <button onClick={() => quickStatus(p, "paid")} className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-green-500/10 text-green-400 border border-green-400/30" data-testid={`mark-paid-${p.id}-mobile`}>Paid</button>
                  <button onClick={() => quickStatus(p, "unpaid")} className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-slate-500/10 text-slate-400 border border-slate-400/30" data-testid={`mark-unpaid-${p.id}-mobile`}>Unpaid</button>
                  <button onClick={() => quickStatus(p, "partial")} className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-yellow-500/10 text-yellow-400 border border-yellow-400/30" data-testid={`mark-partial-${p.id}-mobile`}>Partial</button>
                  <button onClick={() => quickStatus(p, "waived")} className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-blue-500/10 text-blue-400 border border-blue-400/30" data-testid={`mark-waived-${p.id}-mobile`}>Waive</button>
                  <button onClick={() => setEditing({ ...p })} className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]" data-testid={`edit-${p.id}-mobile`}><Edit2 size={14} /></button>
                  <button onClick={() => del(p)} className="p-1.5 hover:bg-red-500/10 rounded text-red-400" data-testid={`delete-${p.id}-mobile`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block kr-card rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#14213D] uppercase text-[10px] tracking-widest text-slate-300">
                <tr>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Paid</th>
                  <th className="text-left p-3">Remaining</th>
                  <th className="text-left p-3">Due</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-white/10" data-testid={`payment-row-${p.id}-desktop`}>
                    <td className="p-3 font-semibold align-top">
                      {p.title}
                      {p.description && <div className="text-xs text-slate-500 mt-0.5">{p.description}</div>}
                    </td>
                    <td className="p-3 text-slate-300 text-xs uppercase tracking-widest align-top">{CAT_LABEL[p.category] || p.category}</td>
                    <td className="p-3 align-top">${Number(p.amount).toFixed(2)}</td>
                    <td className="p-3 text-green-300 align-top">${Number(p.amount_paid || 0).toFixed(2)}</td>
                    <td className="p-3 text-[#00E5FF] align-top">${Number(p.remaining_balance || 0).toFixed(2)}</td>
                    <td className="p-3 text-slate-400 text-xs align-top">{p.due_date || "—"}</td>
                    <td className="p-3 align-top"><StatusBadge status={p.status} /></td>
                    <td className="p-3 align-top">
                      <div className="flex gap-1 flex-wrap">
                        <button onClick={() => quickStatus(p, "paid")} title="Mark paid" className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-green-500/10 text-green-400 border border-green-400/30" data-testid={`mark-paid-${p.id}-desktop`}>Paid</button>
                        <button onClick={() => quickStatus(p, "unpaid")} title="Mark unpaid" className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-slate-500/10 text-slate-400 border border-slate-400/30" data-testid={`mark-unpaid-${p.id}-desktop`}>Unpaid</button>
                        <button onClick={() => quickStatus(p, "partial")} title="Mark partial" className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-yellow-500/10 text-yellow-400 border border-yellow-400/30" data-testid={`mark-partial-${p.id}-desktop`}>Partial</button>
                        <button onClick={() => quickStatus(p, "waived")} title="Waive" className="px-2 py-1 text-[10px] uppercase tracking-widest rounded hover:bg-blue-500/10 text-blue-400 border border-blue-400/30" data-testid={`mark-waived-${p.id}-desktop`}>Waive</button>
                        <button onClick={() => setEditing({ ...p })} title="Edit" className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]" data-testid={`edit-${p.id}-desktop`}><Edit2 size={14} /></button>
                        <button onClick={() => del(p)} title="Archive" className="p-1.5 hover:bg-red-500/10 rounded text-red-400" data-testid={`delete-${p.id}-desktop`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto"
          onClick={() => setEditing(null)}
          data-testid="edit-payment-modal"
        >
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={save}
              className="kr-card rounded-2xl p-6 sm:p-8 max-w-xl w-full my-8 relative space-y-3"
            >
              <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
                <X size={20} />
              </button>
              <div className="eyebrow">{editing.id ? "Edit" : "Add"} Payment Request</div>
              <h2 className="font-display text-2xl uppercase mb-4">{editing.id ? "Update" : "New"} Charge</h2>
              <input required placeholder="Title (e.g., Spring Tournament Fee)" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="pay-title" />
              <textarea placeholder="Description (optional)" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" />
              <div className="grid grid-cols-2 gap-3">
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="pay-category">
                  {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="pay-status">
                  {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input required type="number" step="0.01" placeholder="Amount" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="pay-amount" />
                <input type="number" step="0.01" placeholder="Amount Paid" value={editing.amount_paid} onChange={(e) => setEditing({ ...editing, amount_paid: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="pay-amount-paid" />
                <input type="date" placeholder="Due date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="pay-due" />
                <input type="date" placeholder="Payment date" value={editing.payment_date || ""} onChange={(e) => setEditing({ ...editing, payment_date: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3" />
                <select value={editing.payment_method || ""} onChange={(e) => setEditing({ ...editing, payment_method: e.target.value })} className="bg-[#050A15] border border-white/10 rounded-md p-3 col-span-2">
                  {METHODS.map(([k, l]) => <option key={k} value={k}>{l ? `Method: ${l}` : "Method: —"}</option>)}
                </select>
              </div>
              <textarea placeholder="Admin notes (private — parents don't see)" rows={2} value={editing.admin_notes} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" />
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-primary flex-1 justify-center" data-testid="pay-save"><Save size={14} /> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
